export interface ValidationResult {
  valid: boolean;
  errorEn?: string;
  errorMn?: string;
  code?: 'too_short' | 'too_long' | 'invalid_chars' | 'offensive' | 'scam' | 'reserved' | 'missing_number' | 'missing_letter' | 'invalid_format';
}

import { MAX_NAME_LENGTH } from './protocol.js';

export const MIN_NAME_LENGTH = 1;
export { MAX_NAME_LENGTH };
export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 128;
export const MAX_EMAIL_LENGTH = 120;

// Allowed characters: Latin letters, Mongolian Cyrillic letters, digits, and safe separators (_ - . space)
const ALLOWED_NAME_REGEX = /^[a-zA-Zа-яА-ЯөӨүҮ0-9]+([ _.-][a-zA-Zа-яА-ЯөӨүҮ0-9]+)*$/;

// Scam, URL and contact handles
const SCAM_DOMAIN_REGEX = /\b(https?:\/\/|www\.|\.(com|net|org|xyz|io|me|info|site|app|cc|ru|cn|biz|top|online|vip))\b/i;
const CONTACT_HANDLE_REGEX = /\b(t\.me\/|telegram|viber|whatsapp|wechat|discord)\b/i;
const PHONE_NUMBER_REGEX = /\d{6,}/;

// Reserved words
const RESERVED_NAMES_REGEX = /(?:^|[ _.-])(admin|administrator|moderator|system|support|staff|official|bigtwo|arvangurav)(?:[ _.-]|$)/i;

// Adult / Vulgar / Profanity keywords (English & Mongolian transliteration)
const PROFANITY_LIST = [
  // English adult & vulgar
  'sex',
  'sexy',
  'porn',
  'xxx',
  'nude',
  'naked',
  'penis',
  'dick',
  'vagina',
  'pussy',
  'bitch',
  'slut',
  'whore',
  'fuck',
  'shit',
  'asshole',
  'cunt',
  'cock',
  'bastard',
  'nigger',
  'nigga',
  'hitler',
  'nazi',
  // Mongolian vulgar & slurs
  'pizda',
  'lalvar',
  'golog',
  'novsh',
  'alarchin',
  'boov',
  'boodoi',
  'chiits',
  'zail',
  'huts',
  'shees',
];

/**
 * Normalizes text to detect obfuscated words:
 * e.g., 'p.0.r.n' -> 'porn', 'b!tch' -> 'bitch', 'fuuuuck' -> 'fuck'
 */
function normalizeForProfanityCheck(raw: string): string {
  let normalized = raw.toLowerCase();

  // Leetspeak replacements
  normalized = normalized
    .replace(/@/g, 'a')
    .replace(/0/g, 'o')
    .replace(/1/g, 'i')
    .replace(/3/g, 'e')
    .replace(/5/g, 's')
    .replace(/\$/g, 's')
    .replace(/7/g, 't')
    .replace(/!/g, 'i');

  // Strip all non-alphanumeric characters
  normalized = normalized.replace(/[^a-z0-9а-яөү]/g, '');

  // Collapse consecutive identical characters (e.g., 'ffuuuck' -> 'fuck')
  normalized = normalized.replace(/(.)\1+/g, '$1');

  return normalized;
}

export function validateDisplayName(raw: unknown): ValidationResult {
  if (typeof raw !== 'string' || !raw.trim()) {
    return {
      valid: false,
      code: 'too_short',
      errorEn: 'Please enter a name.',
      errorMn: 'Нэрээ оруулна уу.',
    };
  }

  const name = raw.trim();

  if (name.length < MIN_NAME_LENGTH) {
    return {
      valid: false,
      code: 'too_short',
      errorEn: `Name must be at least ${MIN_NAME_LENGTH} characters.`,
      errorMn: `Нэр хамгийн багадаа ${MIN_NAME_LENGTH} тэмдэгттэй байх ёстой.`,
    };
  }

  if (name.length > MAX_NAME_LENGTH) {
    return {
      valid: false,
      code: 'too_long',
      errorEn: `Name cannot exceed ${MAX_NAME_LENGTH} characters.`,
      errorMn: `Нэр ${MAX_NAME_LENGTH}-оос илүүгүй тэмдэгттэй байх ёстой.`,
    };
  }

  // Check for scam URLs and contact handles
  if (SCAM_DOMAIN_REGEX.test(name) || CONTACT_HANDLE_REGEX.test(name) || PHONE_NUMBER_REGEX.test(name)) {
    return {
      valid: false,
      code: 'scam',
      errorEn: 'Name cannot contain website links, phone numbers, or social handles.',
      errorMn: 'Нэрэнд веб хаяг, утасны дугаар, холбоос оруулах боломжгүй.',
    };
  }

  // Check reserved names
  if (RESERVED_NAMES_REGEX.test(name)) {
    return {
      valid: false,
      code: 'reserved',
      errorEn: 'This name is reserved by the system.',
      errorMn: 'Энэхүү нэрийг ашиглах боломжгүй (системээс нөөцөлсөн).',
    };
  }

  // Check profanity / adult content
  const normalized = normalizeForProfanityCheck(name);
  for (const badWord of PROFANITY_LIST) {
    if (normalized.includes(badWord)) {
      return {
        valid: false,
        code: 'offensive',
        errorEn: 'Name contains inappropriate or offensive language.',
        errorMn: 'Нэрэнд зохисгүй эсвэл хориотой үг орсон байна.',
      };
    }
  }

  if (!ALLOWED_NAME_REGEX.test(name)) {
    return {
      valid: false,
      code: 'invalid_chars',
      errorEn: 'Name may only contain letters, numbers, and allowed characters (- _ .).',
      errorMn: 'Нэр зөвхөн үсэг, тоо болон (- _ .) тэмдэгт агуулж болно.',
    };
  }

  return { valid: true };
}

export function validatePassword(raw: unknown): ValidationResult {
  if (typeof raw !== 'string' || !raw) {
    return {
      valid: false,
      code: 'too_short',
      errorEn: 'Please enter a password.',
      errorMn: 'Нууц үгээ оруулна уу.',
    };
  }

  if (raw.length < MIN_PASSWORD_LENGTH) {
    return {
      valid: false,
      code: 'too_short',
      errorEn: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
      errorMn: `Нууц үг хамгийн багадаа ${MIN_PASSWORD_LENGTH} тэмдэгттэй байх ёстой.`,
    };
  }

  if (raw.length > MAX_PASSWORD_LENGTH) {
    return {
      valid: false,
      code: 'too_long',
      errorEn: `Password cannot exceed ${MAX_PASSWORD_LENGTH} characters.`,
      errorMn: `Нууц үг ${MAX_PASSWORD_LENGTH}-аас ихгүй байх ёстой.`,
    };
  }

  const hasLetter = /[a-zA-Z]/.test(raw);
  const hasDigit = /[0-9]/.test(raw);

  if (!hasLetter) {
    return {
      valid: false,
      code: 'missing_letter',
      errorEn: 'Password must include at least one letter.',
      errorMn: 'Нууц үг дор хаяж 1 үсэг агуулсан байх ёстой.',
    };
  }

  if (!hasDigit) {
    return {
      valid: false,
      code: 'missing_number',
      errorEn: 'Password must include at least one number.',
      errorMn: 'Нууц үг дор хаяж 1 тоо агуулсан байх ёстой.',
    };
  }

  return { valid: true };
}

const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateEmail(raw: unknown): ValidationResult {
  if (typeof raw !== 'string' || !raw.trim()) {
    return {
      valid: false,
      code: 'invalid_format',
      errorEn: 'Please enter an email address.',
      errorMn: 'И-мэйл хаягаа оруулна уу.',
    };
  }

  const email = raw.trim().toLowerCase();

  if (email.length > MAX_EMAIL_LENGTH || !EMAIL_FORMAT_REGEX.test(email)) {
    return {
      valid: false,
      code: 'invalid_format',
      errorEn: 'Please enter a valid email address.',
      errorMn: 'Зөв и-мэйл хаяг оруулна уу.',
    };
  }

  return { valid: true };
}
